import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Phone } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { specialOffers, FALLBACK_PROMO } from '../data/special';
import { OfferCard } from '../components/special/OfferCard';
import { TrustRow, HowItWorks, Reviews } from '../components/special/SpecialSections';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { BookingFlow } from '../components/booking/BookingFlow';

const PROMO_CODE = 'facebook';

const useNoIndex = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Facebook Exclusive | Stefen's Mobile Detailing";
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.title = prevTitle; meta.remove(); };
  }, []);
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

export default function SpecialPage() {
  useNoIndex();
  const [booking, setBooking] = useState(null);
  const [promo, setPromo] = useState(FALLBACK_PROMO);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/booking/promos/${PROMO_CODE}`)
      .then((r) => setPromo(r.data))
      .catch(() => setPromo(FALLBACK_PROMO));
  }, []);

  return (
    <div className="bg-white min-h-screen pb-24 md:pb-0 overflow-x-hidden" data-testid="special-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Stefen's Detailing" className="h-10 sm:h-11 w-auto" />
            <span className="hidden sm:block font-display italic uppercase font-extrabold text-neutral-950">Stefen&apos;s <span className="text-red-600">Detailing</span></span>
          </div>
          <a href={`tel:${businessInfo.phone}`} className="inline-flex items-center gap-2 bg-neutral-950 hover:bg-neutral-800 text-white text-sm font-semibold px-4 py-2 rounded-sm" data-testid="special-header-call">
            <Phone className="w-4 h-4" /><span className="sm:hidden">Call / Text</span><span className="hidden sm:inline">{businessInfo.phone}</span>
          </a>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-5 sm:px-6 pt-9 md:pt-20 pb-8 md:pb-10 text-center animate-fade-in" data-testid="special-hero">
        <div className="inline-flex items-center gap-3 mb-5">
          <span className="h-[3px] w-8 bg-red-600 -skew-x-12"></span>
          <span className="font-display uppercase tracking-[0.25em] text-xs md:text-sm font-semibold text-red-600">Welcome, Facebook neighbors</span>
          <span className="h-[3px] w-8 bg-red-600 -skew-x-12"></span>
        </div>
        <h1 className="font-display italic uppercase text-[2.6rem] sm:text-6xl lg:text-7xl font-extrabold text-neutral-950 leading-[0.95] tracking-tight">
          Two Offers.<br /><span className="text-red-600">One Showroom Finish.</span>
        </h1>
        <p className="text-neutral-600 text-[15px] md:text-lg mt-4 md:mt-6 max-w-2xl mx-auto">
          Thanks for stopping by from our ad. These prices are only for Facebook customers, and we bring everything to your driveway.
        </p>
        <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3 justify-center mt-6 md:mt-8">
          <button type="button" onClick={() => scrollTo('offer-ceramic')} className="px-3 sm:px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-display uppercase tracking-wider font-bold rounded-sm text-sm sm:text-base" data-testid="special-jump-ceramic"><span className="block sm:inline">Ceramic<span className="hidden sm:inline"> Coating ·</span></span> $999</button>
          <button type="button" onClick={() => scrollTo('offer-full-detail')} className="px-3 sm:px-6 py-3.5 bg-neutral-950 hover:bg-neutral-800 text-white font-display uppercase tracking-wider font-bold rounded-sm text-sm sm:text-base" data-testid="special-jump-full-detail"><span className="block sm:inline">Full Detail<span className="hidden sm:inline"> ·</span></span> $249</button>
        </div>
        <div className="mt-7 md:mt-10"><TrustRow /></div>
      </section>

      <section className="bg-neutral-50 py-8 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {specialOffers.map((o) => <OfferCard key={o.id} offer={o} onBook={setBooking} />)}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 items-center" data-testid="special-ceramic-proof">
        <div className="h-72 sm:h-80 md:h-[440px] rounded-sm overflow-hidden shadow-xl">
          <BeforeAfterSlider before="/images/ceramic-before.jpg?v=2" after="/images/ceramic-after.jpg?v=2" alt="Paint correction and ceramic coating" />
        </div>
        <div>
          <span className="font-display uppercase tracking-[0.2em] text-xs font-semibold text-red-600">Drag to see the difference</span>
          <h2 className="font-display italic uppercase text-3xl sm:text-4xl font-extrabold text-neutral-950 mt-2 mb-4">Swirls Out. Shine Locked In.</h2>
          <p className="text-neutral-700 leading-relaxed">
            Our 1-step correction removes 70–95% of scratches, swirl marks and light oxidation, so your paint shines like a mirror with that deep wet look. Then we lock it in with a ceramic coating that protects it for 5 years.
          </p>
          <button type="button" onClick={() => setBooking(specialOffers[0])} className="mt-6 w-full sm:w-auto px-7 py-4 sm:py-3.5 bg-red-600 hover:bg-red-700 text-white font-display uppercase tracking-wider font-bold rounded-sm" data-testid="special-proof-book">Book Ceramic · $999</button>
        </div>
      </section>

      <section className="bg-neutral-50 py-10 md:py-16"><div className="max-w-6xl mx-auto px-5 sm:px-6"><HowItWorks /></div></section>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-16"><Reviews /></section>

      <footer className="bg-neutral-950 border-t-4 border-red-600 py-8 text-center text-white/50 text-sm">
        © {new Date().getFullYear()} {businessInfo.name} · {businessInfo.serviceArea}
      </footer>

      {!booking && (
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-neutral-200 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] grid grid-cols-2 gap-2 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]" data-testid="special-sticky-bar">
        <button type="button" onClick={() => setBooking(specialOffers[0])} className="bg-red-600 active:bg-red-700 text-white font-display uppercase font-bold py-3.5 rounded-sm text-base" data-testid="sticky-book-ceramic">Ceramic $999</button>
        <button type="button" onClick={() => setBooking(specialOffers[1])} className="bg-neutral-950 active:bg-neutral-800 text-white font-display uppercase font-bold py-3.5 rounded-sm text-base" data-testid="sticky-book-full-detail">Full Detail $249</button>
      </div>
      )}

      <BookingFlow
        open={!!booking}
        onClose={() => setBooking(null)}
        serviceName={booking?.serviceName}
        promo={booking?.promo ? promo : null}
      />
    </div>
  );
}
